import { memo, useMemo, useState } from 'react';
import { Odds } from '@/components/atoms/Odds';
import { Td } from '@/components/atoms/TableCells';
import { SortableTh, type SortDirection } from '@/components/atoms/SortableTh';
import { futuresRows, isPolyFutureBoard, type FuturesBoard, type FuturesSelection } from './futuresDetail';
import { TOTAL_VOLUME_LABEL, VOL_HEADER_TOOLTIP_KALSHI } from './sharedBookCue';

interface SeriesTableProps {
  event: FuturesBoard;
  onSelect?: (selection: FuturesSelection) => void;
}

function formatVolume(vol: number | null): string {
  if (vol === null || vol === 0) return '—';
  if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(1)}M`;
  if (vol >= 10_000) return `${(vol / 1_000).toFixed(1)}K`;
  return Math.round(vol).toLocaleString();
}

type SortKey = 'name' | 'price' | 'volume';

export const SeriesTable = memo(function SeriesTable({ event, onSelect }: SeriesTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('price');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const poly = isPolyFutureBoard(event);
  const select = (ticker: string) => onSelect?.({ ticker, sport: event.sport,
    boardKey: event.key, venue: poly ? 'polymarket' : 'kalshi' });
  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'name' ? 'asc' : 'desc'); }
  };
  const sorted = useMemo(() => futuresRows(event).sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortKey === 'name') return a.name.localeCompare(b.name) * dir || a.ticker.localeCompare(b.ticker);
    const av = sortKey === 'price' ? a.price : a.volume;
    const bv = sortKey === 'price' ? b.price : b.volume;
    return (av === null && bv === null ? 0 : av === null ? 1 : bv === null ? -1 : (av - bv) * dir)
      || a.name.localeCompare(b.name) || a.ticker.localeCompare(b.ticker);
  }), [event, sortKey, sortDir]);

  return (
    <section data-view-anchor={`series:${event.key}`} className="bg-card border-border min-w-0 rounded-lg border">
      <div className="border-border flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b px-4 py-3">
        <h2 className="text-foreground min-w-0 break-words text-lg font-semibold">{event.title}</h2>
        <span className="text-muted-foreground text-xs">{poly ? event.tour : event.seriesTicker}</span>
        <span className="text-muted-foreground text-xs">{sorted.length} outcomes</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse border-spacing-0">
          <thead className="bg-muted/50 border-border border-b">
            <tr>
              <SortableTh sortKey="name" activeSortKey={sortKey} activeSortDir={sortDir} onSort={(k) => handleSort(k as SortKey)}>Outcome</SortableTh>
              <SortableTh className="w-40" sortKey="price" activeSortKey={sortKey} activeSortDir={sortDir} onSort={(k) => handleSort(k as SortKey)}>{poly ? 'Polymarket' : 'Kalshi'} · YES</SortableTh>
              <SortableTh className="w-28 text-right" sortKey="volume" activeSortKey={sortKey} activeSortDir={sortDir} onSort={(k) => handleSort(k as SortKey)} title={poly ? 'Venue-reported traded volume, when available.' : VOL_HEADER_TOOLTIP_KALSHI}>{TOTAL_VOLUME_LABEL}</SortableTh>
            </tr>
          </thead>
          <tbody>
            {sorted.map((o) => (
              <tr key={o.ticker} className="border-border/50 hover:bg-muted/30 border-b last:border-b-0">
                <Td className="break-words [overflow-wrap:anywhere]">
                  {onSelect && o.price !== null ? <button type="button" onClick={() => select(o.ticker)} className="text-foreground text-left font-medium hover:underline">{o.name}</button> : <span className="text-foreground font-medium">{o.name}</span>}
                  {o.detail && <span className="text-muted-foreground ml-2 text-xs">{o.detail}</span>}
                </Td>
                <Td>{o.price !== null ? (
                  onSelect ? <button type="button" onClick={() => select(o.ticker)} className="hover:bg-accent rounded px-2 py-1 text-left"><FutureQuote cents={o.price} /></button>
                    : <FutureQuote cents={o.price} />
                ) : <span className="text-muted-foreground">—</span>}</Td>
                <Td className="text-right font-mono text-sm">{formatVolume(o.volume)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {poly && <p className="text-muted-foreground border-border border-t px-4 py-2 text-xs">Indicative YES prices from the feed snapshot, not executable depth. Open an outcome to review its YES/NO limit ticket.</p>}
    </section>
  );
});

function FutureQuote({ cents }: { cents: number }) {
  return <span>{Number(cents.toFixed(4))}¢ | <Odds cents={cents} format="american" /></span>;
}
